import { motion } from "framer-motion";

/**
 * Animated QRIS counter scene — replaces qris_counter photo.
 */
export default function QRISIllustration() {
    return (
        <div className="relative w-full aspect-square rounded-3xl overflow-hidden bg-gradient-to-br from-[#0A192F] via-[#1E3A8A] to-[#10B981]/40">
            {/* Wood-grain counter texture */}
            <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-amber-900/60 to-transparent" />
            <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 400 400">
                <defs>
                    <pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse">
                        <circle cx="10" cy="10" r="0.6" fill="white" />
                    </pattern>
                </defs>
                <rect width="400" height="400" fill="url(#dots)" />
            </svg>

            {/* QRIS Stand */}
            <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="absolute left-1/2 -translate-x-1/2 top-[15%] flex flex-col items-center"
            >
                {/* Stand base */}
                <div className="w-1.5 h-24 bg-slate-700" />
                {/* Stand neck */}
                <div className="w-16 h-2 bg-slate-700 -mt-24" />
                {/* QR holder */}
                <div className="relative -mt-2">
                    <div className="bg-white rounded-2xl p-4 shadow-2xl shadow-black/30 border-2 border-slate-200">
                        {/* QRIS Logo */}
                        <div className="flex items-center gap-1 mb-2">
                            <div className="font-heading font-bold text-[10px] text-rose-500">QR</div>
                            <div className="font-heading font-bold text-[10px] text-slate-800">IS</div>
                        </div>
                        {/* QR Code */}
                        <div className="grid grid-cols-9 gap-[1px] w-24 h-24 bg-slate-100 p-0.5">
                            {Array.from({ length: 81 }).map((_, i) => {
                                // Create QR-like pattern (corners + body)
                                const isCorner =
                                    (i < 21 && (i % 9 < 3 || i % 9 > 5)) ||
                                    (i % 9 < 3 && i > 60);
                                const isCenter = !isCorner && Math.random() > 0.5;
                                return (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.02, delay: 0.5 + i * 0.005 }}
                                        className={isCorner || isCenter ? "bg-slate-900" : "bg-white"}
                                    />
                                );
                            })}
                        </div>
                        <div className="text-[7px] text-center text-slate-500 mt-1 font-semibold">SCAN UNTUK BAYAR</div>
                    </div>
                    {/* Scanning beam */}
                    <motion.div
                        initial={{ top: 0 }}
                        animate={{ top: "100%" }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        className="absolute left-0 right-0 h-0.5 bg-brand-green shadow-[0_0_8px_2px_rgba(16,185,129,0.8)]"
                        style={{ top: 0 }}
                    />
                </div>
            </motion.div>

            {/* EDC machine */}
            <motion.div
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="absolute left-[10%] bottom-[15%]"
            >
                <div className="bg-slate-800 rounded-xl p-2 shadow-xl shadow-black/40 w-20">
                    <div className="bg-emerald-400/20 rounded p-1.5 mb-1.5">
                        <div className="text-[6px] text-emerald-300 font-mono">Total:</div>
                        <div className="text-[8px] text-emerald-300 font-bold font-mono">Rp 50.000</div>
                    </div>
                    {/* Keypad */}
                    <div className="grid grid-cols-3 gap-0.5">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                            <div key={n} className="h-2 bg-slate-700 rounded-sm" />
                        ))}
                    </div>
                </div>
            </motion.div>

            {/* Phone scanning QR */}
            <motion.div
                initial={{ x: 30, opacity: 0, rotate: -10 }}
                animate={{ x: 0, opacity: 1, rotate: [-10, 0, -8, 0] }}
                transition={{
                    x: { duration: 0.6, delay: 0.9 },
                    opacity: { duration: 0.6, delay: 0.9 },
                    rotate: { duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1.5 },
                }}
                className="absolute right-[8%] bottom-[12%] w-16"
            >
                <div className="bg-slate-900 rounded-2xl p-1 shadow-2xl shadow-black/40">
                    <div className="h-1 w-6 rounded-full bg-slate-900 mx-auto" />
                    <div className="rounded-xl bg-gradient-to-br from-brand-green/30 to-brand-green/10 p-1.5 aspect-[9/16] flex flex-col items-center justify-center">
                        <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                            className="h-6 w-6 rounded-lg border-2 border-brand-green"
                        />
                        <div className="text-[5px] text-white mt-1 font-semibold">Scanning...</div>
                    </div>
                </div>
            </motion.div>

            {/* Success ripple */}
            {[0, 1, 2].map((i) => (
                <motion.div
                    key={i}
                    initial={{ scale: 0, opacity: 0.6 }}
                    animate={{ scale: 2, opacity: 0 }}
                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.6, ease: "easeOut" }}
                    className="absolute left-1/2 top-[35%] -translate-x-1/2 h-20 w-20 -translate-y-1/2 rounded-full border-2 border-brand-green"
                />
            ))}

            {/* Floating QR badge */}
            <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: 1.2, type: "spring" }}
                className="absolute top-5 right-5 bg-white/95 dark:bg-card rounded-2xl p-3 shadow-xl border border-border"
            >
                <svg className="h-6 w-6 text-brand-green" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
            </motion.div>
        </div>
    );
}
